import type { BulkSummary, Changed, Kind } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";

/** Each kind's page; review requests and comments have none (their lines link the item). */
export const LINKS: Record<Kind, string | null> = {
  item: "/items/",
  entity: "/entities/",
  rule: "/rules/",
  review: "/reviews/",
  request: null,
  comment: null,
  folder: null,
};

/** A review with its details (not redacted as someone's private content). */
export const isReview = (c: Changed | undefined): c is Changed & { item: string } =>
  c?.kind === "review" && Boolean(c.item);

/** A review request or a comment, with its item (not redacted as someone's private content). */
export const isAboutItem = (c: Changed | undefined): c is Changed & { item: string; kind: "request" | "comment" } =>
  (c?.kind === "request" || c?.kind === "comment") && Boolean(c.item);
/** Their lines: naming the item, or (on its own history) not. */
export const ABOUT_ITEM_TEXT = {
  request: {
    created: ["history.requested", "history.requestedBare"],
    updated: ["history.requestUpdated", "history.requestUpdatedBare"],
    deleted: ["history.requestWithdrawn", "history.requestWithdrawnBare"],
  },
  comment: {
    created: ["history.commented", "history.commentedBare"],
    updated: ["history.commentEdited", "history.commentEditedBare"],
    deleted: ["history.commentDeleted", "history.commentDeletedBare"],
  },
} as const;

/**
 * How an item's change to who sees it is said, with its slot `{label}`: made public, private or the organization's
 * again; else guests added or taken off. None for any other change.
 */
export function sharingText(c: Changed, label: string): string | null {
  const s = c.sharing;
  if (c.kind !== "item" || !s || !c.label) {
    return null;
  }
  if (s.from !== s.to) {
    // To the organization: shared wider (from private), or kept inside it (from public).
    const to = s.to === "org" ? (s.from === "public" ? "orgOnly" : "org") : s.to === "public" ? "public" : "private";
    return t(`history.sharing.${to}`, { label });
  }
  return s.guests_added > 0
    ? t("history.sharing.guestsAdded", { label, count: s.guests_added })
    : t("history.sharing.guestsRemoved", { label, count: s.guests_removed });
}

/** What a change did, as said: in an undo, making something is bringing it back ("Restored item"). */
export const didOf = (c: Changed, undo = false): Changed["action"] | "restored" =>
  undo && c.action === "created" ? "restored" : c.action;

/** "Updated item “PO-1043”" / "Created review" / "Reviewed “PO-1043”: 2 findings" (in an undo, "Restored item …"). */
export function describe(c: Changed, undo = false): string {
  if (isAboutItem(c)) {
    return t(ABOUT_ITEM_TEXT[c.kind][c.action][0], { label: c.label });
  }
  if (isReview(c)) {
    return c.action === "deleted"
      ? t("history.reviewRemoved", { label: c.label })
      : `${t("history.reviewed", { label: c.label })} ${t("item.findings", { count: c.findings ?? 0 })}`;
  }
  const shared = sharingText(c, c.label);
  if (shared) {
    return shared;
  }
  const kind = t(`history.noun.${c.kind}`);
  const did = didOf(c, undo);
  return c.label ? t(`history.did.${did}`, { kind, label: c.label }) : t(`history.didBare.${did}`, { kind });
}

/** How telling a change is, to lead a commit's line: a deletion, then a creation, then the rest (an edit made along
 *  with them, like a link to what was deleted going away). */
const TELLING: Readonly<Record<string, number>> = { deleted: 0, created: 1 };

/** The change a commit's line leads with: its most telling (the first of those). */
export function lead<T extends { action: string }>(preview: readonly T[]): T | undefined {
  return [...preview].sort((a, b) => (TELLING[a.action] ?? 2) - (TELLING[b.action] ?? 2))[0];
}

/** How many of a commit's changes created, edited and deleted something. */
export interface Actions {
  created: number;
  updated: number;
  deleted: number;
}

/**
 * `what` (its lead change) and the rest of a commit's `count` changes: what they did when it's one thing ("and edited
 * 2 more"), else how many ("and 3 more changes").
 */
export function andMore(what: string, first: { action: string }, count: number, actions?: Actions | undefined): string {
  const rest = count - 1;
  const left = actions && actions.created + actions.updated + actions.deleted === count ? { ...actions } : null;
  if (left) {
    left[first.action as keyof Actions] -= 1;
    const only = (["created", "updated", "deleted"] as const).find((a) => left[a] === rest);
    if (only) {
      return t(`history.andMore.${only}`, { what, count: rest });
    }
  }
  return t("history.andMore", { what, count: rest });
}

/** A bulk change in a line, however many items it took: "Archived 5,589 items". */
export const bulkText = (b: BulkSummary): string => t(`history.bulk.${b.action}`, { count: b.items });

/** A commit in one line: a bulk change's, else its message, else its first change (and how many more). */
export function summary(
  c: {
    message?: string | null | undefined;
    reverts?: string | null | undefined;
    preview: readonly Changed[];
    actions?: Actions | undefined;
    bulk?: BulkSummary | null | undefined;
  },
  count: number,
): string {
  if (c.bulk) {
    return bulkText(c.bulk);
  }
  const first = lead(c.preview);
  const undo = Boolean(c.reverts);
  const changes = first
    ? count > 1
      ? andMore(describe(first, undo), first, count, c.actions)
      : describe(first, undo)
    : t("history.changes", { count });
  if (c.message) {
    return c.message;
  }
  // An undo: said so, then what it put back.
  return c.reverts ? `${t("history.undid")} — ${changes}` : changes;
}
